import { CHECK_OUTCOME_DISPLAY } from "../vocabulary";
import type { CheckOutcome } from "../types";

/**
 * The five-outcome cell encoding (§5.4, fixed): pass solid green · finding
 * solid red · not-checkable amber DIAGONAL-STRIPED · not-applicable solid
 * gray · not run white OUTLINED. Stripes and outline keep the five outcomes
 * distinct in grayscale (§3.3) — color is never the sole carrier.
 */
const STRIPES = {
  backgroundImage:
    "repeating-linear-gradient(45deg, #F59E0B 0px, #F59E0B 2px, #FEF3C7 2px, #FEF3C7 5px)",
};

export function OutcomeSquare({
  outcome,
  title,
  onClick,
  size = "md",
}: {
  outcome: CheckOutcome;
  /** Hover hint — drawn by `TooltipLayer`, not by the browser. */
  title?: string;
  onClick?: () => void;
  size?: "sm" | "md";
}) {
  const dim = size === "sm" ? "w-3 h-3" : "w-4 h-4";
  const base = `inline-block ${dim} rounded-[3px] shrink-0 align-middle`;
  const label = title ?? CHECK_OUTCOME_DISPLAY[outcome];
  const common = { "data-tip": label, onClick } as const;
  switch (outcome) {
    case "pass":
      return <span {...common} className={`${base} bg-[#10B981]`} />;
    case "finding":
      return <span {...common} className={`${base} bg-[#EF4444]`} />;
    case "not-checkable":
      return (
        <span
          {...common}
          className={`${base} border border-amber-400`}
          style={STRIPES}
        />
      );
    case "not-applicable":
      return <span {...common} className={`${base} bg-gray-400`} />;
    case "not-run":
      return (
        <span {...common} className={`${base} bg-white border border-gray-400`} />
      );
  }
  return null;
}

const LEGEND_ORDER: CheckOutcome[] = [
  "pass",
  "finding",
  "not-checkable",
  "not-applicable",
  "not-run",
];

/** The matrix always renders its legend (§3.3). Optional counts render the
 *  same geometry as page 6's standing-level card. */
export function OutcomeLegend({
  counts,
}: {
  counts?: Partial<Record<CheckOutcome, number>>;
}) {
  return (
    <div className="flex items-center gap-4 flex-wrap">
      {LEGEND_ORDER.map((outcome) => (
        <span key={outcome} className="inline-flex items-center gap-1.5">
          <OutcomeSquare outcome={outcome} size="sm" />
          <span className="text-xs text-gray-500">
            {CHECK_OUTCOME_DISPLAY[outcome]}
            {counts ? ` ${counts[outcome] ?? 0}` : ""}
          </span>
        </span>
      ))}
    </div>
  );
}
